// src/pages/reporting/transactions.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { alpha, styled, useTheme } from '@mui/material/styles';

const API_BASE_URL = 'https://suprapacific.ibanking.ltd/api/report/0/';
const DEFAULT_DATE_RANGE = 'all';

const statuses = ['All', 'SUCCESS', 'PENDING', 'FAILED', 'REFUNDED'];
const sortOptions = [
  { label: 'Newest first', value: 'date-desc' },
  { label: 'Oldest first', value: 'date-asc' },
  { label: 'Amount high → low', value: 'amount-desc' },
  { label: 'Amount low → high', value: 'amount-asc' },
];

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const statusMeta = {
  SUCCESS: { color: 'success', icon: CheckCircleOutlineIcon },
  PENDING: { color: 'warning', icon: PendingOutlinedIcon },
  FAILED: { color: 'error', icon: ErrorOutlineIcon },
  REFUNDED: { color: 'info', icon: AutorenewOutlinedIcon },
};

const MetricCard = styled(Card)(() => ({ height: '100%' }));

const normalizeString = (value, fallback = '—') => value || fallback;
const parseAmount = (value) => Number.parseFloat(value) || 0;

const parseDateValue = (value) => {
  if (!value) return null;
  const num = Number(value);
  const d = Number.isFinite(num) ? new Date(num) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getTxnDate = (txn) =>
  parseDateValue(txn.payment_completion_time) ||
  parseDateValue(txn.created_at) ||
  parseDateValue(txn.order_created_at);

const formatDate = (txn) => {
  const d = getTxnDate(txn);
  return d ? dateFormatter.format(d) : '—';
};

const getTxnTime = (txn) => getTxnDate(txn)?.getTime() ?? 0;

const getBankRef = (t) => {
  if (!t.bank_reference) return null;
  if (typeof t.bank_reference === 'object') return t.bank_reference.bank_ref_num ?? null;
  return t.bank_reference;
};

const getPaymentMode = (t) => {
  if (!t.payment_method) return null;
  if (typeof t.payment_method === 'object') return t.payment_method.mode ?? null;
  return t.payment_method;
};

const getGateway = (t) => t.gateway ?? getPaymentMode(t) ?? null;

const showRepost = (txn) => !txn.reciept_no && txn.payment_status === 'SUCCESS';

const normalizeTransaction = (t) => ({
  ...t,
  payment_status: t.payment_status?.toUpperCase() ?? 'UNKNOWN',
});

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

function StatusChip({ status }) {
  const theme = useTheme();
  const meta = statusMeta[status] || { color: 'default', icon: PaymentsOutlinedIcon };
  const Icon = meta.icon;

  return (
    <Chip
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={status}
      color={meta.color}
      variant="outlined"
      size="small"
      sx={{
        fontWeight: 600,
        backgroundColor: alpha(
          theme.palette[meta.color]?.main || theme.palette.text.primary,
          0.06
        ),
      }}
    />
  );
}

function PaymentPathModal({ open, onClose, transaction: t }) {
  if (!t) return null;

  const displayDate = getTxnDate(t);

  const steps = [
    {
      icon: <PersonOutlinedIcon sx={{ fontSize: 26, color: 'primary.main' }} />,
      label: 'Customer',
      sub: normalizeString(t.customer_id),
      detail: normalizeString(t.customer_number),
      done: true,
      status: 'SUCCESS',
    },
    {
      icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 26, color: 'primary.main' }} />,
      label: 'Gateway',
      sub: normalizeString(getGateway(t)),
      detail: `Bank Ref: ${normalizeString(getBankRef(t))}`,
      done: true,
      status: 'SUCCESS',
    },
    {
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 26, color: 'primary.main' }} />,
      label: 'Loan Account',
      sub: normalizeString(t.loan_number),
      detail: `Order ID: ${normalizeString(t.order_id)}`,
      done: t.payment_status === 'SUCCESS' || t.payment_status === 'REFUNDED',
      status: t.payment_status,
    },
    {
      icon: (
        <PaymentsOutlinedIcon
          sx={{ fontSize: 26, color: t.reciept_no ? 'success.main' : 'text.disabled' }}
        />
      ),
      label: 'Receipt',
      sub: normalizeString(t.reciept_no),
      detail: t.reciept_no ? 'Receipt generated' : 'Receipt pending',
      done: Boolean(t.reciept_no),
      status: t.reciept_no ? 'SUCCESS' : 'PENDING',
    },
  ];

  const stepStatusIcon = (step) => {
    if (step.status === 'FAILED') {
      return <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    }
    if (step.status === 'REFUNDED') {
      return <AutorenewOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />;
    }
    if (step.status === 'SUCCESS') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    if (step.done) {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    return <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Payment Path
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {normalizeString(t.order_id)} · {displayDate ? dateFormatter.format(displayDate) : '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close modal">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
          sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {currencyFormatter.format(parseAmount(t.payment_amount))}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box mt={0.5}>
              <StatusChip status={t.payment_status} />
            </Box>
          </Box>
        </Stack>

        {steps.map((step, idx) => (
          <Box key={step.label}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    border: '2px solid',
                                        borderColor: step.done ? 'primary.main' : 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 1,
                  }}
                >
                  {step.icon}
                </Box>
                {idx < steps.length - 1 && (
                  <Box
                    sx={{ width: 2, height: 32, bgcolor: 'primary.main', opacity: 0.25, mt: 0.5 }}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1, pt: 0.75, pb: idx < steps.length - 1 ? 1 : 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {step.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.sub}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {step.detail}
                    </Typography>
                  </Box>
                  {stepStatusIcon(step)}
                </Stack>
              </Box>
            </Stack>
          </Box>
        ))}

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {[
            { label: 'Order ID', value: t.order_id },
            { label: 'Customer ID', value: t.customer_id },
            { label: 'Loan Number', value: t.loan_number },
            { label: 'Receipt No', value: t.reciept_no },
            { label: 'Gateway', value: getGateway(t) },
            { label: 'Attempt', value: t.attempt },
            { label: 'Module', value: t.module_code },
            { label: 'Bank Ref', value: getBankRef(t) },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {normalizeString(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default function TransactionsPage() {
  const isMobile = useMediaQuery('(max-width:900px)');

  const [searchDraft, setSearchDraft] = useState('');
  const [searchByDraft, setSearchByDraft] = useState('customer_id');
  const [statusDraft, setStatusDraft] = useState('All');
  const [sortDraft, setSortDraft] = useState('date-desc');
  const [dateRangeDraft, setDateRangeDraft] = useState(DEFAULT_DATE_RANGE);
  const [fromDateDraft, setFromDateDraft] = useState('');
  const [toDateDraft, setToDateDraft] = useState('');

  const [query, setQuery] = useState('');
  const [searchBy, setSearchBy] = useState('customer_id');
  const [status, setStatus] = useState('All');
  const [sort, setSort] = useState('date-desc');
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalTxn, setModalTxn] = useState(null);

  const effectiveFrom = useMemo(() => {
    if (dateRange === 'all') return null;
    if (dateRange === 'today') return startOfDay(new Date());
    if (dateRange === 'last7') {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return startOfDay(d);
    }
    if (dateRange === 'thisMonth') {
      const now = new Date();
      return startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    }
    if (dateRange === 'lastMonth') {
      const now = new Date();
      return startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    }
    if (dateRange === 'custom' && fromDate) return startOfDay(new Date(fromDate));
    return null;
  }, [dateRange, fromDate]);

  const effectiveTo = useMemo(() => {
    if (dateRange === 'all') return null;
    if (dateRange === 'today') return endOfDay(new Date());
    if (dateRange === 'last7') return endOfDay(new Date());
    if (dateRange === 'thisMonth') return endOfDay(new Date());
    if (dateRange === 'lastMonth') {
      const now = new Date();
      return endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    if (dateRange === 'custom' && toDate) return endOfDay(new Date(toDate));
    return null;
  }, [dateRange, toDate]);

  const buildUrl = (value, mode) => {
    const url = new URL(API_BASE_URL);
    if (value.trim()) url.searchParams.set(mode, value.trim());
    return url.toString();
  };

  const fetchTransactions = async (searchValue = query, searchMode = searchBy) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(buildUrl(searchValue, searchMode));
      if (!response.ok) throw new Error(`API failed with status ${response.status}`);
      const result = await response.json();
      const payload = (result?.data?.results || []).map(normalizeTransaction);
      setTransactions(payload);
      setApiCount(result?.data?.count || payload.length);
      setPage(0);
    } catch (err) {
      setTransactions([]);
      setApiCount(0);
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions('', 'customer_id');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const items = transactions.filter((t) => {
      const haystack = [
        t.order_id,
        t.customer_id,
        t.customer_number,
        t.loan_number,
        getBankRef(t),
        getGateway(t),
        t.payment_status,
        t.module_code,
        t.reciept_no,
      ]
        .join(' ')
        .toLowerCase();

      const txnDateRaw = getTxnDate(t);
      const txnDate = txnDateRaw ? startOfDay(txnDateRaw) : null;

      return (
        haystack.includes(query.toLowerCase()) &&
        (status === 'All' || t.payment_status === status) &&
        (!effectiveFrom || (txnDate && txnDate >= effectiveFrom)) &&
        (!effectiveTo || (txnDate && txnDate <= effectiveTo))
      );
    });

    items.sort((a, b) => {
      if (sort === 'amount-asc') {
        return parseAmount(a.payment_amount) - parseAmount(b.payment_amount);
      }
      if (sort === 'amount-desc') {
        return parseAmount(b.payment_amount) - parseAmount(a.payment_amount);
      }
      if (sort === 'date-asc') {
        return getTxnTime(a) - getTxnTime(b);
      }
      return getTxnTime(b) - getTxnTime(a);
    });

    return items;
  }, [transactions, query, status, sort, effectiveFrom, effectiveTo]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totals = useMemo(
    () =>
      transactions.reduce(
        (acc, t) => {
          acc.total += 1;
          if (t.payment_status === 'SUCCESS') acc.success += 1;
          else if (t.payment_status === 'PENDING') acc.pending += 1;
          else if (t.payment_status === 'FAILED') acc.failed += 1;
          return acc;
        },
        { total: 0, success: 0, pending: 0, failed: 0 }
      ),
    [transactions]
  );

  const handleResetAll = () => {
    setSearchDraft('');
    setSearchByDraft('customer_id');
    setStatusDraft('All');
    setSortDraft('date-desc');
    setDateRangeDraft(DEFAULT_DATE_RANGE);
    setFromDateDraft('');
    setToDateDraft('');

    setQuery('');
    setSearchBy('customer_id');
    setStatus('All');
    setSort('date-desc');
    setDateRange(DEFAULT_DATE_RANGE);
    setFromDate('');
    setToDate('');
    fetchTransactions('', 'customer_id');
  };

  const handleSearchApply = () => {
    const clean = searchDraft.trim();
    setQuery(clean);
    setSearchBy(searchByDraft);
    fetchTransactions(clean, searchByDraft);
  };

  const handleProceedFilters = () => {
    const clean = searchDraft.trim();
    setQuery(clean);
    setSearchBy(searchByDraft);
    setStatus(statusDraft);
    setSort(sortDraft);
    setDateRange(dateRangeDraft);
    setFromDate(fromDateDraft);
    setToDate(toDateDraft);
    setPage(0);
    fetchTransactions(clean, searchByDraft);
  };

  const handleExport = () => {
    if (!filtered.length) {
      alert('No data to export.');
      return;
    }

    const header = [
      'id',
      'order_id',
      'payment_status',
      'payment_amount',
      'customer_id',
      'customer_number',
      'loan_number',
      'bank_reference',
      'gateway',
      'payment_method',
      'module_code',
      'attempt',
      'reciept_no',
      'payment_completion_time',
      'created_at',
      'order_created_at',
    ];
        const rows = filtered.map((t) =>
      [
        t.id,
        t.order_id,
        t.payment_status,
        t.payment_amount,
        t.customer_id,
        t.customer_number,
        t.loan_number,
        getBankRef(t),
        getGateway(t),
        getPaymentMode(t),
        t.module_code,
        t.attempt,
        t.reciept_no,
        t.payment_completion_time,
        t.created_at,
        t.order_created_at,
      ]
        .map((v) => `"${String(v ?? '').replaceAll('"', '""')}"`)
        .join(',')
    );

    const blob = new Blob([[header.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRepost = (txn) => {
    alert(`Repost triggered for ${txn.order_id}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ mb: 0.5 }}>
                Transactions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Live report data from API with customer and order based search.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button startIcon={<RefreshIcon />} variant="outlined" onClick={handleResetAll}>
                Reset all
              </Button>
              <Button startIcon={<DownloadIcon />} variant="contained" onClick={handleExport}>
                Export
              </Button>
            </Stack>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <MetricCard>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Loaded records
                </Typography>
                <Typography variant="h5">{totals.total}</Typography>
                <Typography variant="caption" color="text.secondary">
                  API count: {apiCount}
                </Typography>
              </CardContent>
            </MetricCard>
            <MetricCard>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Successful
                </Typography>
                <Typography variant="h5" color="success.main">
                  {totals.success}
                </Typography>
              </CardContent>
            </MetricCard>
            <MetricCard>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Pending
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {totals.pending}
                </Typography>
              </CardContent>
            </MetricCard>
            <MetricCard>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h5" color="error.main">
                  {totals.failed}
                </Typography>
              </CardContent>
            </MetricCard>
          </Box>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <FormControl sx={{ minWidth: 170 }}>
                    <InputLabel>Search by</InputLabel>
                    <Select
                      value={searchByDraft}
                      label="Search by"
                      onChange={(e) => setSearchByDraft(e.target.value)}
                    >
                      <MenuItem value="customer_id">Customer ID</MenuItem>
                      <MenuItem value="order_id">Order ID</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                    <TextField
                      fullWidth
                      value={searchDraft}
                      onChange={(e) => setSearchDraft(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchApply()}
                      placeholder={
                        searchByDraft === 'customer_id'
                          ? 'Enter customer id...'
                          : 'Enter order id...'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSearchApply}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Search
                    </Button>
                  </Box>

                  <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusDraft}
                      label="Status"
                      onChange={(e) => setStatusDraft(e.target.value)}
                    >
                      {statuses.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>Date range</InputLabel>
                    <Select
                      value={dateRangeDraft}
                      label="Date range"
                      onChange={(e) => {
                        const v = e.target.value;
                        setDateRangeDraft(v);
                        if (v !== 'custom') {
                          setFromDateDraft('');
                          setToDateDraft('');
                        }
                      }}
                    >
                      <MenuItem value="all">All time</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="last7">Last 7 days</MenuItem>
                      <MenuItem value="thisMonth">This month</MenuItem>
                      <MenuItem value="lastMonth">Last month</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>

                  {dateRangeDraft === 'custom' && (
                    <>
                      <TextField
                        label="From date"
                        type="date"
                        value={fromDateDraft}
                        onChange={(e) => setFromDateDraft(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                      />
                      <TextField
                        label="To date"
                        type="date"
                        value={toDateDraft}
                        onChange={(e) => setToDateDraft(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                      />
                    </>
                  )}
                                    <FormControl sx={{ minWidth: 210 }}>
                    <InputLabel>Sort</InputLabel>
                    <Select
                      value={sortDraft}
                      label="Sort"
                      onChange={(e) => setSortDraft(e.target.value)}
                    >
                      {sortOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ flexGrow: 1 }} />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleProceedFilters}
                    sx={{ alignSelf: { xs: 'stretch', md: 'flex-end' }, whiteSpace: 'nowrap' }}
                  >
                    Proceed filters
                  </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Showing {filtered.length} of {transactions.length} loaded records
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 6 }}
                >
                  <CircularProgress size={26} />
                  <Typography color="text.secondary">Loading transactions...</Typography>
                </Stack>
              </CardContent>
            </Card>
          ) : isMobile ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              {paged.map((t) => (
                <Card key={t.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {normalizeString(t.order_id)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Customer: {normalizeString(t.customer_id)}
                          </Typography>
                        </Box>
                        <StatusChip status={t.payment_status} />
                      </Stack>

                      <Typography variant="h6">
                        {currencyFormatter.format(parseAmount(t.payment_amount))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Loan: {normalizeString(t.loan_number)}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={normalizeString(getGateway(t))} size="small" variant="outlined" />
                        <Chip
                          label={normalizeString(t.module_code)}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        {normalizeString(getBankRef(t))} • {formatDate(t)}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Receipt: {normalizeString(t.reciept_no)}
                      </Typography>

                      <Stack direction="row" spacing={1} pt={0.5}>
                        {showRepost(t) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<ReplayIcon />}
                            onClick={() => handleRepost(t)}
                          >
                            Repost
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={() => setModalTxn(t)}
                        >
                          View
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {!paged.length && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary">
                      No transactions match the current filters.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gateway</TableCell>
                    <TableCell>Loan No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Receipt No</TableCell>
                    <TableCell align="center">Repost</TableCell>
                    <TableCell align="center">View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{normalizeString(t.order_id)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {normalizeString(getBankRef(t))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{normalizeString(t.customer_id)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {normalizeString(t.customer_number)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {currencyFormatter.format(parseAmount(t.payment_amount))}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={t.payment_status} />
                      </TableCell>
                      <TableCell>{normalizeString(getGateway(t))}</TableCell>
                      <TableCell>{normalizeString(t.loan_number)}</TableCell>
                      <TableCell>{formatDate(t)}</TableCell>
                      <TableCell>
                        {t.reciept_no ? (
                          <Typography variant="body2" fontWeight={600}>
                            {t.reciept_no}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {showRepost(t) ? (
                          <Tooltip title="Repost this transaction" arrow>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleRepost(t)}
                              sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                            >
                              Repost
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View payment path" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setModalTxn(t)}
                            aria-label={`View payment path for ${t.order_id}`}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!paged.length && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No transactions match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Stack>
      </Container>

      <PaymentPathModal
        open={Boolean(modalTxn)}
        onClose={() => setModalTxn(null)}
        transaction={modalTxn}
      />
    </Box>
  );
}